import type {
  DataImport,
  ImportHealthInsight,
  Dataset,
  DatasetAssistance,
  DatasetField,
  ImportConfig,
  PreparedImportPreview,
  ImportRepairPlan,
  ImportReviewRows,
} from '@lognai/plugin-analytics';
import { apiFetch, apiPost } from '../api/client';
export const listDatasets = () => apiFetch<{ items: Dataset[] }>('/api/analytics/datasets');
export const getImportInsight = () =>
  apiPost<ImportHealthInsight>('/api/analytics/imports/insight', {});
export const listImports = () => apiFetch<{ items: DataImport[] }>('/api/analytics/imports');
export const deleteImport = (id: string) =>
  apiFetch<{ deleted: true }>(`/api/analytics/imports/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
export const getImport = (id: string) => apiFetch<DataImport>(`/api/analytics/imports/${id}`);
export const getDataset = (id: string) =>
  apiFetch<Dataset>(`/api/analytics/datasets/${encodeURIComponent(id)}`);
export const getDatasetSample = (id: string, version: number) =>
  apiFetch<{ dataset: Dataset; rows: Record<string, unknown>[] }>(
    `/api/analytics/datasets/${id}/sample?version=${version}`
  );
export const getDatasetUsage = (id: string) =>
  apiFetch<{ items: Array<{ id: string; title: string; scope: string }>; more: boolean }>(
    `/api/analytics/datasets/${id}/usage`
  );
export const getDatasetAssistance = (
  kind: 'import' | 'dataset',
  id: string,
  input: {
    question?: string;
    fields?: DatasetField[];
    fieldId?: string;
    source?: string;
    ai?: boolean;
    purpose?: 'preparation' | 'validation';
  } = {}
) =>
  apiPost<DatasetAssistance>(
    `/api/analytics/${kind === 'import' ? 'imports' : 'datasets'}/${id}/assistance`,
    input
  );
export const uploadDataset = (file: File) => {
  const body = new FormData();
  body.append('file', file);
  return apiFetch<DataImport>('/api/analytics/imports', { method: 'POST', body });
};
export const configureImport = (id: string, config: ImportConfig) =>
  apiPost<DataImport>(`/api/analytics/imports/${id}/configure`, config);
export const getPreparedImportPreview = (id: string, config: ImportConfig) =>
  apiPost<PreparedImportPreview>(`/api/analytics/imports/${id}/prepared-preview`, {
    fields: config.fields,
    source: config.source,
    skipEmptyRows: config.skipEmptyRows,
    excludedColumns: config.excludedColumns,
    timezone: config.timezone,
    nullValues: config.nullValues,
  });
export const importAction = (id: string, action: 'validate' | 'publish' | 'retry' | 'cancel') =>
  apiPost(`/api/analytics/imports/${id}/${action}`, {});
export const datasetHistory = (id: string) =>
  apiFetch<{ items: Dataset[] }>(`/api/analytics/datasets/${id}/versions`);
export const publishDataset = (id: string) => apiPost(`/api/analytics/datasets/${id}/publish`, {});
export const deleteDataset = (id: string) =>
  apiFetch(`/api/analytics/datasets/${id}`, { method: 'DELETE' });

export const getModuleContext = () =>
  apiFetch<{ modules: Array<{ id: string; available: boolean; missing: string[] }> }>(
    '/api/analytics/datasets/module-context'
  );

export const getImportRepairPlan = (id: string) =>
  apiFetch<ImportRepairPlan>(`/api/analytics/imports/${id}/repair-plan`);
export const applyImportRepairs = (
  id: string,
  revision: string,
  selections: Array<{ id: string; option?: string; replacements?: Record<string, string> }>
) => apiPost<DataImport>(`/api/analytics/imports/${id}/repairs/apply`, { revision, selections });
export const undoImportRepairs = (id: string, revision: string) =>
  apiPost<DataImport>(`/api/analytics/imports/${id}/repairs/undo`, { revision });
export const getImportReviewRows = (
  id: string,
  query: {
    revision: string;
    source: string;
    view: 'issues' | 'changed';
    issueId?: string;
    offset?: number;
  }
) => {
  const params = new URLSearchParams(
    Object.entries(query)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  );
  return apiFetch<ImportReviewRows>(`/api/analytics/imports/${id}/review-rows?${params}`);
};

export const getFixReview = (id: string) =>
  apiFetch<{ events: import('@lognai/plugin-analytics').FixReviewEvent[] }>(
    `/api/analytics/imports/${id}/fix-review`
  );
export const suggestImportFix = (
  id: string,
  input: { revision: string; issueId?: string; instruction?: string; remember?: boolean }
) =>
  apiPost<{
    advice: DatasetAssistance;
    events: import('@lognai/plugin-analytics').FixReviewEvent[];
  }>(`/api/analytics/imports/${id}/fix-review/suggest`, input);
export const decideImportFix = (
  id: string,
  input: {
    revision: string;
    repairId: string;
    before?: string;
    option?: string;
    after?: string;
    decision: 'accepted' | 'edited' | 'rejected';
  }
) =>
  apiPost<import('@lognai/plugin-analytics').FixReviewEvent>(
    `/api/analytics/imports/${id}/fix-review/decision`,
    input
  );
export const getDatasetExploration = (id: string, version?: number) =>
  apiFetch<{ exploration?: import('@lognai/plugin-analytics').DatasetExploration }>(
    `/api/analytics/datasets/${id}/exploration${version ? `?version=${version}` : ''}`
  );
export const startDatasetExploration = (id: string, retry = false) =>
  apiPost<{ exploration?: import('@lognai/plugin-analytics').DatasetExploration }>(
    `/api/analytics/datasets/${id}/exploration`,
    { retry }
  );
export const reviewChartProposal = (
  datasetId: string,
  proposalId: string,
  input: {
    revision: number;
    action: 'approve' | 'reject' | 'restore' | 'edit' | 'revise';
    instruction?: string;
    definition?: import('@lognai/plugin-analytics').ChartProposal['definition'];
  }
) =>
  apiPost<import('@lognai/plugin-analytics').ChartProposal>(
    `/api/analytics/datasets/${datasetId}/chart-proposals/${proposalId}/review`,
    input
  );
